import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { CashmereModule } from './cashmere.module';
import { NgSelectModule } from '@ng-select/ng-select';
import { GridStatesComponent } from './components/grid-states.component';
import { RequiredMarkerDirective } from './directives/required-marker.directive';
import { GridColMenuComponent } from './components/grid-col-menu/grid-col-menu.component';
import { GridBulkActionsComponent } from './components/grid-bulk-actions/grid-bulk-actions.component';
import { ErrorPageComponent } from './components/error/error-page.component';
import { CronEditorComponent } from './components/cron-editor/cron-editor.component';
import { ActiveStatusDisplayComponent } from './components/active-status-display/active-status-display.component';
import { IngestionJobsListComponent } from './components/ingestion-jobs-list/ingestion-jobs-list.component';
import BaseComponent from './components/base.component';
import { ConfirmationModalComponent } from './components/confirmation-modal/confirmation-modal.component';
import {
    FilterChipsPanelComponent,
    FilterItemComponent,
    MinMaxComponent,
    MultiSelectComponent,
    SingleSelectComponent,
    YesNoSelectComponent,
    DatePickerComponent
} from './components/filter';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { GridCellComponent } from './components/grid-cell/grid-cell.component';
import { ScheduleJobsComponent } from './components/schedule-jobs/schedule-jobs.component';
import { SearchHighlightPipe } from './pipes/search-highlight.pipe';
import { TrimDirective } from './directives/input-trim.directive';
import { SelectJobsGridComponent } from './components/select-jobs-grid/select-jobs-grid.component';
import { JobChipComponent } from './components/job-chip/job-chip.component';
import { ScheduleSelectComponent } from './components/schedule-select/schedule-select.component';
import { SelectSummaryComponent } from './components/select-summary/select-summary.component';
import { WatermarkChangeWarningChipComponent } from './components/watermark-change-warning-chip/watermark-change-warning-chip.component';
import { RouterModule } from '@angular/router';
import { BaseDatePipe } from './pipes/base-date.pipe';
import { HideIfFeatureFlagOffDirective } from './directives/hide-if-feature-flag-off.directive';
import { TenantSwitcherComponent } from './components/tenant-switcher/tenant-switcher.component';
import { TenantSwitcherSplashComponent } from './components/tenant-switcher-splash/tenant-switcher-splash.component';
import { ContactComponent } from '../modules/app-catalog/view/contacts/contact/contact.component';
import { ContactInitialsPipe } from '../modules/app-catalog/view/contacts/contact/contact-initials.pipe';
import { TemplateListComponent } from './components/template-list/template-list.component';
import { TenantSidenavComponent } from './components/tenant-sidenav/tenant-sidenav.component';
import { TenantsSelectionComponent } from './components/tenant-selection/tenant-selection.component';
import { BaseUrlService } from './services/base-url.service';
import { NewTabService } from './services/new-tab.service';
import { BreadcrumbsComponent } from './components/breadcrumbs/breadcrumbs.component';
import { PromoterTypeSelectComponent } from './components/promoter-type-select/promoter-type-select.component';
import { PromoterTypeWarningChipComponent } from './components/promoter-type-select/promoter-type-warning-chip/promoter-type-warning-chip.component';
import { IsBatchedRawModePipe } from './pipes/is-batched-raw-mode.pipe';
import { IsFullLoadRawModePipe } from './pipes/is-full-load-raw-mode.pipe';
import { SelectJobsSummaryComponent } from './components/select-jobs-summary/select-jobs-summary.component';
import { JobReassignWarningComponent } from './components/job-reassign-warning/job-reassign-warning.component';
import { IconSelectorComponent } from './components/icon-selector/icon-selector.component';
import { GridHeaderCellComponent } from './components/grid-header-cell/grid-header-cell.component';
import { RelativeTimeRangeSelectorComponent } from './components/relative-time-range-selector/relative-time-range-selector.component';
import { SourceSwitcherComponent } from './components/ingest-source-switcher/source-switcher.component';
import { JobRunSupportLevelSelectorComponent } from './components/job-run-support-level-selector/job-run-support-level-selector.component';
import { AppCardComponent } from './components/app-card/app-card.component';
import { AppActionButtonStylePipe } from './pipes/app-action-button-style/app-action-button-style.pipe';
import { AppActionButtonTitlePipe } from './pipes/app-action-button-style/app-action-button-title.pipe';
import { IsIconCustomPipe } from './pipes/is-custom-icon.pipe';
import { AppInitialsPipe } from './pipes/app-initials.pipe';
import { AppMiniCardComponent } from './components/app-mini-card/app-mini-card.component';
import {
    CheckboxMultiselectComponent,
    CheckboxMultiselectOptionTemplateDirective
} from './components/multiselect/checkbox-multiselect.component';
import { HasUserPermissionsDirective } from './directives/has-user-permissions.directive';
import { AppIconComponent } from './components/app-icon/app-icon.component';
import { IFrameComponent } from './components/iframe.component';
import { FilterPanelComponent } from './components/filter/filter-panel/filter-panel.component';
import { FilterDrawerButtonComponent } from './components/filter/filter-drawer-button/filter-drawer-button.component';
import { NotAuthorizedPageComponent } from './components/not-authorized/not-authorized-page.component';

@NgModule({
    imports: [
        NgSelectModule,
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        CashmereModule,
        DragDropModule,
        RouterModule
    ],
    declarations: [
        AppIconComponent,
        RelativeTimeRangeSelectorComponent,
        GridStatesComponent,
        GridColMenuComponent,
        GridHeaderCellComponent,
        GridCellComponent,
        ActiveStatusDisplayComponent,
        JobChipComponent,
        GridBulkActionsComponent,
        RequiredMarkerDirective,
        TrimDirective,
        ErrorPageComponent,
        CronEditorComponent,
        IconSelectorComponent,
        IngestionJobsListComponent,
        BaseComponent,
        ConfirmationModalComponent,
        FilterPanelComponent,
        FilterItemComponent,
        MinMaxComponent,
        MultiSelectComponent,
        SingleSelectComponent,
        YesNoSelectComponent,
        DatePickerComponent,
        FilterChipsPanelComponent,
        FilterDrawerButtonComponent,
        ScheduleJobsComponent,
        ScheduleSelectComponent,
        SelectSummaryComponent,
        SelectJobsSummaryComponent,
        SourceSwitcherComponent,
        JobReassignWarningComponent,
        TenantSwitcherComponent,
        TenantSwitcherSplashComponent,
        SearchHighlightPipe,
        BaseDatePipe,
        IsBatchedRawModePipe,
        IsFullLoadRawModePipe,
        SelectJobsGridComponent,
        WatermarkChangeWarningChipComponent,
        HideIfFeatureFlagOffDirective,
        CheckboxMultiselectOptionTemplateDirective,
        TemplateListComponent,
        ContactInitialsPipe,
        ContactComponent,
        TenantsSelectionComponent,
        TenantSidenavComponent,
        BreadcrumbsComponent,
        PromoterTypeSelectComponent,
        PromoterTypeWarningChipComponent,
        JobRunSupportLevelSelectorComponent,
        AppCardComponent,
        AppMiniCardComponent,
        CheckboxMultiselectComponent,
        AppActionButtonStylePipe,
        AppActionButtonTitlePipe,
        IsIconCustomPipe,
        AppInitialsPipe,
        HasUserPermissionsDirective,
        IFrameComponent,
        NotAuthorizedPageComponent
    ],
    exports: [
        CashmereModule,
        NgSelectModule,
        DragDropModule,
        AppIconComponent,
        RelativeTimeRangeSelectorComponent,
        GridStatesComponent,
        GridColMenuComponent,
        GridHeaderCellComponent,
        GridCellComponent,
        ActiveStatusDisplayComponent,
        JobChipComponent,
        GridBulkActionsComponent,
        RequiredMarkerDirective,
        ReactiveFormsModule,
        TrimDirective,
        BaseDatePipe,

        ContactInitialsPipe,
        IsBatchedRawModePipe,
        IsFullLoadRawModePipe,
        ContactComponent,
        ErrorPageComponent,
        CronEditorComponent,
        IconSelectorComponent,
        IngestionJobsListComponent,
        ConfirmationModalComponent,
        FilterPanelComponent,
        FilterItemComponent,
        MinMaxComponent,
        MultiSelectComponent,
        SingleSelectComponent,
        FilterChipsPanelComponent,
        FilterDrawerButtonComponent,
        YesNoSelectComponent,
        DatePickerComponent,
        ScheduleJobsComponent,
        ScheduleSelectComponent,
        SelectSummaryComponent,
        SelectJobsSummaryComponent,
        SourceSwitcherComponent,
        JobReassignWarningComponent,
        TenantSwitcherComponent,
        SearchHighlightPipe,
        SelectJobsGridComponent,
        WatermarkChangeWarningChipComponent,
        HideIfFeatureFlagOffDirective,
        CheckboxMultiselectOptionTemplateDirective,
        TemplateListComponent,
        TenantsSelectionComponent,
        TenantSidenavComponent,
        BreadcrumbsComponent,
        PromoterTypeSelectComponent,
        PromoterTypeWarningChipComponent,
        JobRunSupportLevelSelectorComponent,
        AppCardComponent,
        AppMiniCardComponent,
        CheckboxMultiselectComponent,
        AppActionButtonStylePipe,
        AppActionButtonTitlePipe,
        IsIconCustomPipe,
        AppInitialsPipe,
        HasUserPermissionsDirective,
        IFrameComponent,
        NotAuthorizedPageComponent
    ],
    providers: [
        BaseUrlService,
        NewTabService,
        {
            provide: DatePipe,
            useClass: BaseDatePipe
        }
    ]
})
export class SharedModule {}
